"use client";

import React, { useState, useEffect, useCallback } from "react";
import dynamic from "next/dynamic";
import { PlusIcon, TrashIcon, Article, CheckIcon } from "@phosphor-icons/react";
import { Tooltip } from "@heroui/tooltip";
import { useOptionalToolsSharedCaseData } from "./tools-shared-case-data";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
dayjs.extend(relativeTime);
const MarkdownEditor = dynamic(() => import("./ui/markdownEditor"), {
  ssr: false
});
const RESEARCH_NOTES_STORAGE_PREFIX = "tpl-research-notes";
export function getResearchNotesStorageKey(caseId: string | undefined): string {
  return `${RESEARCH_NOTES_STORAGE_PREFIX}-${caseId || "no-case"}`;
}
export interface ResearchNote {
  id: string;
  title: string;
  content: string;
  createdAt: number;
  updatedAt: number;
}
function loadResearchNotes(storageKey: string): ResearchNote[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = localStorage.getItem(storageKey);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as ResearchNote[];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}
function defaultNote(): ResearchNote {
  const now = Date.now();
  return {
    id: crypto.randomUUID(),
    title: "Untitled note",
    content: "",
    createdAt: now,
    updatedAt: now
  };
}

/** Hook for research notes state, keyed by caseId. */
export function useResearchNotesState(caseId: string | undefined) {
  const storageKey = getResearchNotesStorageKey(caseId);
  const remote = useOptionalToolsSharedCaseData();
  const useRemoteNotes = Boolean(caseId && remote && remote.caseId === caseId);
  const [localNotes, setLocalNotes] = useState<ResearchNote[]>(() => useRemoteNotes ? [] : loadResearchNotes(storageKey));
  const [selectedId, setSelectedId] = useState<string | null>(null);
  useEffect(() => {
    if (!useRemoteNotes) {
      setLocalNotes(loadResearchNotes(storageKey));
    }
    setSelectedId(null);
  }, [storageKey, useRemoteNotes]);
  const notes = useRemoteNotes ? remote!.hydrated ? remote!.notes as ResearchNote[] : [] : localNotes;
  const setNotes = useRemoteNotes ? remote!.setNotes : setLocalNotes;
  useEffect(() => {
    if (useRemoteNotes) return;
    const handler = (event: StorageEvent) => {
      if (event.key === storageKey && event.newValue != null) {
        try {
          const parsed = JSON.parse(event.newValue) as ResearchNote[];
          if (Array.isArray(parsed)) {
            setLocalNotes(parsed);
          }
        } catch {
          /* ignore */
        }
      }
    };
    window.addEventListener("storage", handler);
    return () => window.removeEventListener("storage", handler);
  }, [storageKey, useRemoteNotes]);
  const persist = useCallback((next: ResearchNote[]) => {
    if (useRemoteNotes) {
      return;
    }
    try {
      localStorage.setItem(storageKey, JSON.stringify(next));
    } catch {
      /* ignore */
    }
  }, [useRemoteNotes, storageKey]);
  const addNote = useCallback((): string => {
    const note = defaultNote();
    setNotes(prev => {
      const next = [note, ...prev];
      persist(next);
      return next;
    });
    setSelectedId(note.id);
    return note.id;
  }, [persist, setNotes]);
  const updateNote = useCallback((id: string, updates: {
    title?: string;
    content?: string;
  }) => {
    const now = Date.now();
    setNotes(prev => {
      const idx = prev.findIndex(n => n.id === id);
      if (idx < 0) return prev;
      const next = [...prev];
      next[idx] = {
        ...next[idx],
        ...(updates.title !== undefined && {
          title: updates.title
        }),
        ...(updates.content !== undefined && {
          content: updates.content
        }),
        updatedAt: now
      };
      persist(next);
      return next;
    });
  }, [persist, setNotes]);
  const deleteNote = useCallback((id: string) => {
    setNotes(prev => {
      const next = prev.filter(n => n.id !== id);
      persist(next);
      return next;
    });
    setSelectedId(prev => prev === id ? null : prev);
  }, [persist, setNotes]);
  const remoteRefetch = remote?.refetch;
  const refetch = useCallback(() => {
    if (useRemoteNotes && remoteRefetch) {
      void remoteRefetch();
      return;
    }
    setLocalNotes(loadResearchNotes(storageKey));
  }, [useRemoteNotes, remoteRefetch, storageKey]);
  const selectedNote = notes.find(n => n.id === selectedId) ?? notes[0];
  return {
    notes,
    selectedId,
    setSelectedId,
    selectedNote,
    addNote,
    updateNote,
    deleteNote,
    refetch
  };
}
const TITLE_MAX_LENGTH = 60;
function noteListTitle(note: ResearchNote): string {
  const t = (note.title || "").trim();
  if (t) return t.length <= TITLE_MAX_LENGTH ? t : t.slice(0, TITLE_MAX_LENGTH) + "…";
  return "Untitled note";
}
export interface ResearchNotesContentProps {
  notes: ResearchNote[];
  selectedId: string | null;
  selectedNote: ResearchNote | null;
  onSelectNote: (id: string) => void;
  onAddNote: () => void;
  onUpdateNote: (id: string, updates: {
    title?: string;
    content?: string;
  }) => void;
  onDeleteNote: (id: string) => void;
  /** When the event modal is open for editing, applies note body to the Description field. */
  applyTextToEventDescription?: (text: string) => boolean;
  eventDescriptionApplyReady?: boolean;
}

/** Research Notes view: list of notes + WYSIWYG editor for the selected note. */
export function ResearchNotesContent({
  notes,
  selectedId,
  selectedNote,
  onSelectNote,
  onAddNote,
  onUpdateNote,
  onDeleteNote,
  applyTextToEventDescription,
  eventDescriptionApplyReady = false
}: ResearchNotesContentProps) {
  const [appliedToDescriptionNoteId, setAppliedToDescriptionNoteId] = useState<string | null>(null);
  const handleApplyNoteToEventDescription = useCallback((note: ResearchNote) => {
    if (!applyTextToEventDescription || !eventDescriptionApplyReady) return;
    const text = note.content ?? "";
    const ok = applyTextToEventDescription(text);
    if (!ok) return;
    setAppliedToDescriptionNoteId(note.id);
    setTimeout(() => setAppliedToDescriptionNoteId(null), 1500);
  }, [applyTextToEventDescription, eventDescriptionApplyReady]);
  return <div className="flex flex-1 min-h-0 flex-row overflow-hidden" data-sentry-component="ResearchNotesContent" data-sentry-source-file="research-notes.tsx">
      <div className="flex w-[240px] shrink-0 flex-col border-r border-white/20 overflow-hidden">
        <div className="shrink-0 px-3 py-2 border-b border-white/20 flex flex-row items-center justify-between gap-2">
          <span className="text-white/80 font-light text-[13px] tracking-wider">
            Notes
          </span>
          <button type="button" onClick={onAddNote} className="flex items-center justify-center p-1.5 rounded text-white/70 hover:text-white hover:bg-white/10 focus:outline-none focus:ring-2 focus:ring-white/30 cursor-pointer" aria-label="New note">
            <PlusIcon size={18} weight="bold" data-sentry-element="PlusIcon" data-sentry-source-file="research-notes.tsx" />
          </button>
        </div>
        <div className="flex-1 min-h-0 overflow-y-auto">
          {notes.length === 0 ? <p className="px-3 py-4 text-white/50 text-xs">
              No notes yet. Create one to get started.
            </p> : <ul className="flex flex-col gap-0 list-none pl-0">
              {notes.map(note => <li key={note.id} className={`flex items-start gap-2 border-b border-white/10 px-3 py-2 cursor-pointer group ${selectedNote?.id === note.id ? "bg-white/10" : "hover:bg-white/5"}`} onClick={() => onSelectNote(note.id)}>
                  <div className="min-w-0 flex-1">
                    <p className="text-white/90 text-xs line-clamp-2 break-words">
                      {noteListTitle(note)}
                    </p>
                    <p className="text-white/50 text-[11px] mt-1">
                      {dayjs(note.updatedAt).fromNow()}
                    </p>
                  </div>
                  <Tooltip content={eventDescriptionApplyReady ? "Replace the open event’s Description with this note’s content" : "Open an event for editing to apply this note to Description"} placement="left" delay={0} classNames={{
              content: "bg-tooltip-bg text-white px-3 py-2 rounded-md text-sm max-w-[240px]",
              base: "before:bg-tooltip-bg"
            }}>
                    <button type="button" onClick={e => {
                e.stopPropagation();
                handleApplyNoteToEventDescription(note);
              }} disabled={!eventDescriptionApplyReady} className="rounded p-1 shrink-0 opacity-0 group-hover:opacity-100 text-white/60 hover:bg-white/10 hover:text-white focus:outline-none focus:ring-2 focus:ring-white/30 cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed" aria-label="Apply note to event description">
                      {appliedToDescriptionNoteId === note.id ? <CheckIcon size={14} weight="bold" className="text-green-500" /> : <Article size={14} weight="duotone" />}
                    </button>
                  </Tooltip>
                  <button type="button" onClick={e => {
              e.stopPropagation();
              onDeleteNote(note.id);
            }} className="rounded p-1 shrink-0 opacity-0 group-hover:opacity-100 text-white/60 hover:bg-white/10 hover:text-white focus:outline-none focus:ring-2 focus:ring-white/30 cursor-pointer" aria-label="Delete note">
                    <TrashIcon size={14} weight="duotone" />
                  </button>
                </li>)}
            </ul>}
        </div>
      </div>
      <div className="flex flex-1 min-w-0 flex-col p-3 overflow-hidden">
        {selectedNote ? <>
            <input type="text" value={selectedNote.title} onChange={e => onUpdateNote(selectedNote.id, {
          title: e.target.value
        })} onBlur={e => {
          const t = e.target.value.trim();
          if (!t) onUpdateNote(selectedNote.id, {
            title: "Untitled note"
          });
        }} placeholder="Note title" className="w-full mb-2 px-2 py-1.5 text-base font-medium text-white bg-white/5 border border-white/10 rounded focus:outline-none focus:ring-2 focus:ring-white/30 focus:border-white/20 placeholder-white/40" aria-label="Note title" />
            <div className="flex flex-col flex-1 min-h-0 overflow-hidden">
              <React.Suspense fallback={<div className="text-white/60 text-sm">Loading editor...</div>}>
                <MarkdownEditor key={selectedNote.id} id={`research-note-${selectedNote.id}`} markdown={selectedNote.content} onChange={content => onUpdateNote(selectedNote.id, {
              content
            })} debounceMs={300} />
              </React.Suspense>
            </div>
          </> : <div className="flex flex-1 items-center justify-center text-white/50 text-sm">
            Select a note or create one
          </div>}
      </div>
    </div>;
}